import { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import { Observable } from 'rxjs'
import { filter } from 'rxjs/operators'
import { createGrpcAuthenticator } from 'infra/grpc-authenticator'
import { enableDevTools } from 'utils/grpc-util'
import memoize from 'memoizee'
import { APIConfig } from 'types/definitions'
import {
  CreatePromptResponse,
  CreatePromptRequest,
  GetPromptRequest,
  ListPromptsForJobRequest,
  AnswerPromptRequest,
  DeletePromptRequest,
} from '@taxfyle/api-internal/internal/prompt_pb'
import { PromptServicePromiseClient } from '@taxfyle/api-internal/internal/prompt_grpc_web_pb'

/**
 * Creates an instance of the Prompts API.
 *
 * @param config
 * @param events$
 */
export function createPromptsAPI(
  config: APIConfig,
  events$: Observable<RealtimeEvent>,
) {
  return new PromptsAPI(config, events$)
}

/**
 * Prompts API.
 */
export class PromptsAPI {
  private readonly promptsClient = createPromptsClient(this.config.baseURL)

  private readonly grpcAuthenticator = createGrpcAuthenticator(this.config)

  /**
   * Stream of events for prompts.
   */
  readonly events$: Observable<RealtimeEvent>

  constructor(
    private config: APIConfig,
    events$: Observable<RealtimeEvent>,
  ) {
    this.events$ = events$.pipe(
      filter((event) => {
        switch (event.getTypeCase()) {
          case RealtimeEvent.TypeCase.PROMPT_CREATED:
          case RealtimeEvent.TypeCase.PROMPT_ANSWERED:
          case RealtimeEvent.TypeCase.PROMPT_DELETED:
            return true
        }

        return false
      }),
    )
  }

  /**
   * Creates and sends a prompt, as per the given request.
   */
  async createPrompt(
    request: CreatePromptRequest,
  ): Promise<CreatePromptResponse> {
    return this.promptsClient.createPrompt(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Retrieves the prompt, as per the given request.
   */
  async getPrompt(request: GetPromptRequest) {
    return this.promptsClient.getPrompt(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Retrieves all prompts on a given job.
   */
  async listPromptsForJob(request: ListPromptsForJobRequest) {
    return this.promptsClient.listPromptsForJob(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Answers a prompt
   */
  async answerPrompt(request: AnswerPromptRequest) {
    return this.promptsClient.answerPrompt(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Deletes a prompt
   */
  async deletePrompt(request: DeletePromptRequest) {
    return this.promptsClient.deletePrompt(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }
}

/**
 * Creates a prompts client that is memoized.
 */
const createPromptsClient = memoize(function createPromptsGrpcClient(
  baseUrl: string,
) {
  const client = new PromptServicePromiseClient(baseUrl)
  enableDevTools(client)
  return client
})
